import { useMemo, useCallback } from "react";

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gap: '4px'
  },
  dow: {
    fontSize: '12px',
    fontWeight: '600',
    textAlign: 'center',
    padding: '6px 0'
  },
  dowSunday: { color: '#ef4444' },
  dowSaturday: { color: '#3b82f6' },
  dowDefault: { color: '#6b7280' },
  day: {
    minHeight: '78px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#e5e7eb',
    borderRadius: '8px',
    padding: '6px 8px',
    backgroundColor: '#ffffff',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    boxSizing: 'border-box',
    overflow: 'hidden'
  },
  dayEmpty: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    cursor: 'default'
  },
  dayToday: {
    borderColor: '#2563eb',
    boxShadow: 'inset 0 0 0 1px #2563eb'
  },
  daySelected: {
    backgroundColor: '#eff6ff',
    borderColor: '#2563eb'
  },
  dayNumber: {
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  dayNumberSunday: { color: '#ef4444' },
  dayNumberSaturday: { color: '#3b82f6' },
  dayNumberDefault: { color: '#6b7280' },
  dayNumberOtherMonth: { color: '#d1d5db' },
  todayBadge: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#1d4ed8',
    backgroundColor: '#dbeafe',
    borderRadius: '4px',
    padding: '1px 5px'
  },
  amount: {
    fontWeight: '700',
    color: '#1f2937',
    marginTop: '4px',
    fontVariantNumeric: 'tabular-nums'
  },
  count: {
    color: '#9ca3af',
    fontSize: '11px'
  },
  note: {
    fontSize: '11px',
    marginTop: '2px',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap'
  },
  noteRefund: { color: '#7c3aed' },
  noteCanceled: { color: '#dc2626' },
  noteReady: { color: '#b45309' },
  noteFailed: { color: '#be123c' }
};

const WEEK_DAYS = ['일', '월', '화', '수', '목', '금', '토'];

const createEmptyDayData = (day, dateString, isCurrentMonth = false) => ({
  day,
  dateString,
  isCurrentMonth,
  amount: 0,
  count: 0,
  paidAmount: 0,
  paidCount: 0,
  canceledAmount: 0,
  refundAmount: 0,
  refundCount: 0,
  refundRequestAmount: 0,
  readyAmount: 0,
  processingAmount: 0,
  failedAmount: 0
});

const formatDateString = (year, month, day) =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const SalesCalendar = ({ year, month, dailyData, selectedDate, onDateClick }) => {
  const todayString = useMemo(() => {
    const today = new Date();
    return formatDateString(today.getFullYear(), today.getMonth() + 1, today.getDate());
  }, []);

  const dailyDataMap = useMemo(() => {
    const map = new Map();
    dailyData?.forEach(d => map.set(d.date, d));
    return map;
  }, [dailyData]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const prevMonthLastDay = new Date(year, month - 1, 0).getDate();

    const days = [];

    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const prevMonthNum = month === 1 ? 12 : month - 1;
      const prevYearNum = month === 1 ? year - 1 : year;
      days.push(createEmptyDayData(day, formatDateString(prevYearNum, prevMonthNum, day)));
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = formatDateString(year, month, day);
      const dayData = dailyDataMap.get(dateString);

      days.push({
        day,
        dateString,
        isCurrentMonth: true,
        amount: dayData?.amount || 0,
        count: dayData?.count || 0,
        paidAmount: dayData?.paid_amount || 0,
        paidCount: dayData?.paid_count || 0,
        canceledAmount: dayData?.canceled_amount || 0,
        refundAmount: dayData?.refund_amount || 0,
        refundCount: dayData?.refund_count || 0,
        refundRequestAmount: dayData?.refund_request_amount || 0,
        readyAmount: dayData?.ready_amount || 0,
        processingAmount: dayData?.processing_amount || 0,
        failedAmount: dayData?.failed_amount || 0
      });
    }

    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextMonthNum = month === 12 ? 1 : month + 1;
      const nextYearNum = month === 12 ? year + 1 : year;
      days.push(createEmptyDayData(day, formatDateString(nextYearNum, nextMonthNum, day)));
    }

    return days;
  }, [year, month, dailyDataMap]);

  const handleDateClick = useCallback((dateString, isCurrentMonth) => {
    if (isCurrentMonth) {
      onDateClick(dateString);
    }
  }, [onDateClick]);

  const getDayNumberStyle = useCallback((isCurrentMonth, dayIdx) => {
    if (!isCurrentMonth) return styles.dayNumberOtherMonth;
    if (dayIdx === 0) return styles.dayNumberSunday;
    if (dayIdx === 6) return styles.dayNumberSaturday;
    return styles.dayNumberDefault;
  }, []);

  return (
    <div style={styles.grid} role="grid" aria-label={`${year}년 ${month}월 매출 달력`}>
      {WEEK_DAYS.map((day, idx) => (
        <div
          key={day}
          style={{
            ...styles.dow,
            ...(idx === 0 ? styles.dowSunday : idx === 6 ? styles.dowSaturday : styles.dowDefault)
          }}
        >
          {day}
        </div>
      ))}
      {calendarDays.map((dayInfo, idx) => {
        const dayIdx = idx % 7;
        const isToday = dayInfo.dateString === todayString && dayInfo.isCurrentMonth;
        const isSelected = dayInfo.dateString === selectedDate && dayInfo.isCurrentMonth;
        const hasSales = dayInfo.isCurrentMonth && dayInfo.paidAmount > 0;
        const notes = dayInfo.isCurrentMonth ? [
          dayInfo.refundAmount > 0 && { key: 'refund', style: styles.noteRefund, text: `환불 -${dayInfo.refundAmount.toLocaleString()}` },
          dayInfo.canceledAmount > 0 && { key: 'canceled', style: styles.noteCanceled, text: `취소 ${dayInfo.canceledAmount.toLocaleString()}` },
          dayInfo.refundRequestAmount > 0 && { key: 'refund_request', style: styles.noteCanceled, text: `환불요청 ${dayInfo.refundRequestAmount.toLocaleString()}` },
          dayInfo.readyAmount > 0 && { key: 'ready', style: styles.noteReady, text: `대기 ${dayInfo.readyAmount.toLocaleString()}` },
          dayInfo.processingAmount > 0 && { key: 'processing', style: styles.noteReady, text: `처리중 ${dayInfo.processingAmount.toLocaleString()}` },
          dayInfo.failedAmount > 0 && { key: 'failed', style: styles.noteFailed, text: `실패 ${dayInfo.failedAmount.toLocaleString()}` }
        ].filter(Boolean) : [];

        return (
          <div
            key={dayInfo.dateString}
            role="gridcell"
            style={{
              ...styles.day,
              ...(dayInfo.isCurrentMonth ? {} : styles.dayEmpty),
              ...(isToday ? styles.dayToday : {}),
              ...(isSelected ? styles.daySelected : {})
            }}
            aria-selected={isSelected}
            onClick={() => handleDateClick(dayInfo.dateString, dayInfo.isCurrentMonth)}
            onMouseEnter={(e) => {
              if (dayInfo.isCurrentMonth) e.currentTarget.style.backgroundColor = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : dayInfo.isCurrentMonth ? '#ffffff' : 'transparent';
            }}
          >
            <div style={{ ...styles.dayNumber, ...getDayNumberStyle(dayInfo.isCurrentMonth, dayIdx) }}>
              {dayInfo.day}
              {isToday && <span style={styles.todayBadge}>오늘</span>}
            </div>
            {hasSales && (
              <>
                <div style={styles.amount}>{dayInfo.paidAmount.toLocaleString()}</div>
                <div style={styles.count}>{dayInfo.paidCount || dayInfo.count}건</div>
              </>
            )}
            {notes.map((n) => (
              <div key={n.key} style={{ ...styles.note, ...n.style }}>{n.text}</div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default SalesCalendar;
